import { przezmorzeApi, throwMessageOnBadRequestStatus, throwMessageOnStatuses } from "../../../api/api";
import { przezmorzePublicApi } from "../../../api/publicApi";
import { type ParticipantListStatus } from "./participantListStatus";

export interface ResourceResponse {
  readonly product: { readonly id: string; readonly name: string };
  readonly option: { readonly id: string; readonly name: string };
  readonly date: string;
}

export interface TemplateRoom {
  readonly numberOfPeople: number;
  readonly count: number;
  readonly resources: ResourceResponse[];
}

export interface ParticipantListTemplateCombination {
  readonly rooms: TemplateRoom[];
}

export interface ParticipantListRequiredFields {
  readonly gender: boolean;
  readonly dateOfBirth: boolean;
  readonly citizenship: boolean;
  readonly documentNumber: boolean;
  readonly documentExpiryDate: boolean;
}

export interface SpreadsheetExportTarget {
  readonly providerId: string;
  readonly providerName: string;
  readonly bookingNumber: string;
  readonly hasRecipientEmails: boolean;
}

export interface ParticipantListTemplateOrder {
  readonly name: string;
  readonly userFriendlyId: number;
}

export interface ParticipantListTemplateResponse {
  readonly order: ParticipantListTemplateOrder;
  readonly combinations: ParticipantListTemplateCombination[];
  readonly requiredFields: ParticipantListRequiredFields;
  readonly isBusRequired: boolean;
  readonly spreadsheetExportTargets: SpreadsheetExportTarget[];
}

export type SendToProviderOutcome = "SENT" | "SKIPPED_NO_RECIPIENTS";

export interface SendProviderResult {
  readonly providerId: string;
  readonly providerName: string;
  readonly outcome: SendToProviderOutcome;
}

export interface SendParticipantListToProvidersResponse {
  readonly participantList: ParticipantListResponse;
  readonly sendResults: SendProviderResult[];
}

export interface ParticipantListCombinationResponse {
  [numberOfPeople: string]: {
    readonly rooms: ParticipantListCombinationRoom[];
    readonly resources: ResourceResponse[];
  };
}

export interface ParticipantListResponse {
  readonly id: string;
  readonly createdAt: string;
  readonly status: ParticipantListStatus;
  readonly combinations: ParticipantListCombinationResponse[];
  readonly bus?: {
    readonly plateNumber: string;
    readonly driverPhone?: string;
  };
  readonly tourLeader: {
    readonly tourLeaderPhone?: string;
  };
}

type PersonType = "PARTICIPANT" | "DRIVER" | "TOUR_LEADER";

export interface ParticipantListCombinationPerson {
  readonly type: PersonType;
  readonly name: string;
  readonly surname: string;
  readonly dateOfBirth?: string;
  readonly gender?: string;
  readonly citizenship?: string;
  readonly documentNumber?: string;
  readonly documentExpiryDate?: string;
}

export interface ParticipantListCombinationRoom {
  readonly people: (ParticipantListCombinationPerson | null)[];
}

export interface AddParticipantListCombinationRequest {
  [numberOfPeople: string]: ParticipantListCombinationRoom[];
}

export interface AddParticipantListRequest {
  readonly combinations: AddParticipantListCombinationRequest[];
  readonly bus?: {
    readonly plateNumber: string;
    readonly driverPhone?: string;
  };
  readonly tourLeader: {
    readonly tourLeaderPhone?: string;
  };
}

export async function getParticipantListTemplateForOrder(
  orderId: string,
  abortSignal?: AbortSignal,
): Promise<ParticipantListTemplateResponse> {
  const response = await przezmorzeApi.get<ParticipantListTemplateResponse>(
    `/orders/${orderId}/participant-list-template`,
    { signal: abortSignal },
  );
  return response.data;
}

export async function getParticipantListTemplateWithSignedLink(
  orderId: string,
  signedLinkToken: string,
  abortSignal?: AbortSignal,
): Promise<ParticipantListTemplateResponse> {
  const response = await przezmorzePublicApi.get<ParticipantListTemplateResponse>(
    `/orders/${orderId}/participant-list-template`,
    { signal: abortSignal, params: { token: signedLinkToken } },
  );
  return response.data;
}

export async function getParticipantListTemplateXlsx(orderId: string, abortSignal?: AbortSignal) {
  const response = await przezmorzeApi.get<Blob>(`/orders/${orderId}/participant-list-template/spreadsheet`, {
    signal: abortSignal,
    responseType: "blob",
  });
  return response.data;
}

export async function getParticipantListTemplateXlsxWithSignedLink(
  orderId: string,
  signedLinkToken: string,
  abortSignal?: AbortSignal,
) {
  const response = await przezmorzePublicApi.get<Blob>(`/orders/${orderId}/participant-list-template/spreadsheet`, {
    signal: abortSignal,
    params: { token: signedLinkToken },
    responseType: "blob",
  });
  return response.data;
}

export async function getParticipantListsForOrder(
  orderId: string,
  abortSignal?: AbortSignal,
): Promise<ParticipantListResponse[]> {
  const response = await przezmorzeApi.get<{ items: ParticipantListResponse[] }>(
    `/orders/${orderId}/participant-lists`,
    { signal: abortSignal },
  );
  return response.data.items;
}

export async function getParticipantListsWithSignedLink(
  orderId: string,
  signedLinkToken: string,
  abortSignal?: AbortSignal,
): Promise<ParticipantListResponse[]> {
  const response = await przezmorzePublicApi.get<{ items: ParticipantListResponse[] }>(
    `/orders/${orderId}/participant-lists`,
    { signal: abortSignal, params: { token: signedLinkToken } },
  );
  return response.data.items;
}

export async function getParticipantListVersionZip(orderId: string, version: number, abortSignal?: AbortSignal) {
  const response = await przezmorzeApi.get<Blob>(`/orders/${orderId}/participant-lists/${version}/spreadsheets`, {
    signal: abortSignal,
    headers: { Accept: "application/zip" },
    responseType: "blob",
  });
  return response.data;
}

export async function getParticipantListVersionSpreadsheet(
  orderId: string,
  listVersionNumber: number,
  bookingNumber: string,
  abortSignal?: AbortSignal,
) {
  const encodedBooking = encodeURIComponent(bookingNumber);
  const response = await przezmorzeApi.get<Blob>(
    `/orders/${orderId}/participant-lists/${listVersionNumber}/bookings/${encodedBooking}/spreadsheet`,
    {
      signal: abortSignal,
      headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
      responseType: "blob",
    },
  );
  return response.data;
}

export async function getLatestParticipantListVersionSpreadsheetForBooking(
  orderId: string,
  bookingNumber: string,
  abortSignal?: AbortSignal,
) {
  const encodedBooking = encodeURIComponent(bookingNumber);
  const response = await przezmorzeApi.get<Blob>(
    `/orders/${orderId}/participant-lists/latest/${encodedBooking}/spreadsheet`,
    {
      signal: abortSignal,
      headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
      responseType: "blob",
    },
  );
  return response.data;
}

export async function addParticipantListToOrder(
  orderId: string,
  request: AddParticipantListRequest,
  abortSignal?: AbortSignal,
): Promise<ParticipantListResponse> {
  try {
    const response = await przezmorzeApi.post<ParticipantListResponse>(
      `/orders/${orderId}/participant-lists`,
      request,
      { signal: abortSignal },
    );
    return response.data;
  } catch (error) {
    throwMessageOnStatuses(error, [400, 401], "Nieprawidłowe dane listy uczestników");
  }
}

export async function addParticipantListWithSignedLink(
  orderId: string,
  signedLinkToken: string,
  request: AddParticipantListRequest,
  abortSignal?: AbortSignal,
): Promise<ParticipantListResponse> {
  try {
    const response = await przezmorzePublicApi.post<ParticipantListResponse>(
      `/orders/${orderId}/participant-lists`,
      request,
      { signal: abortSignal, params: { token: signedLinkToken } },
    );
    return response.data;
  } catch (error) {
    throwMessageOnStatuses(error, [400, 401], "Nieprawidłowe dane listy uczestników");
  }
}

export async function uploadParticipantListSpreadsheet(orderId: string, file: File): Promise<ParticipantListResponse> {
  const formData = new FormData();
  formData.append("file", file, file.name);
  try {
    const response = await przezmorzeApi.post<ParticipantListResponse>(
      `/orders/${orderId}/participant-lists/upload`,
      formData,
    );
    return response.data;
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409]);
  }
}

export async function uploadParticipantListSpreadsheetWithSignedLink(
  orderId: string,
  signedLinkToken: string,
  file: File,
): Promise<ParticipantListResponse> {
  const formData = new FormData();
  formData.append("file", file, file.name);
  try {
    const response = await przezmorzePublicApi.post<ParticipantListResponse>(
      `/orders/${orderId}/participant-lists/upload`,
      formData,
      { params: { token: signedLinkToken } },
    );
    return response.data;
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409]);
  }
}

export type SendToProvidersRequest =
  { sendToAll: true } | { sendToAll: false; bookings: { providerId: string; bookingNumber: string }[] };

export async function sendParticipantListToProviders(
  orderId: string,
  listVersionNumber: number,
  body: SendToProvidersRequest,
): Promise<SendParticipantListToProvidersResponse> {
  try {
    const response = await przezmorzeApi.post<SendParticipantListToProvidersResponse>(
      `/orders/${orderId}/participant-lists/${listVersionNumber}/send-to-providers`,
      body,
    );
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function updateParticipantListStatus(
  orderId: string,
  listVersionNumber: number,
  status: ParticipantListStatus,
  abortSignal?: AbortSignal,
): Promise<ParticipantListResponse> {
  try {
    const response = await przezmorzeApi.put<ParticipantListResponse>(
      `/orders/${orderId}/participant-lists/${listVersionNumber}/status`,
      { status },
      { signal: abortSignal },
    );
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

// --- Signed link admin (ADMIN / AGENT only) ---

export interface ParticipantListLinkRow {
  readonly id: string;
  readonly createdAt: string;
  readonly createdByEmail: string;
  readonly revokedAt: string | null;
  readonly lastUsedAt: string | null;
}

export interface ParticipantListLinkCreated {
  readonly id: string;
  readonly token: string;
}

export async function createParticipantListLink(
  orderId: string,
  abortSignal?: AbortSignal,
): Promise<ParticipantListLinkCreated> {
  try {
    const response = await przezmorzeApi.post<ParticipantListLinkCreated>(
      `/orders/${orderId}/participant-lists/links`,
      {},
      { signal: abortSignal },
    );
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function listParticipantListLinks(
  orderId: string,
  abortSignal?: AbortSignal,
): Promise<ParticipantListLinkRow[]> {
  const response = await przezmorzeApi.get<{ items: ParticipantListLinkRow[] }>(
    `/orders/${orderId}/participant-lists/links`,
    {
      signal: abortSignal,
    },
  );
  return response.data.items;
}

export async function revokeParticipantListLink(
  orderId: string,
  linkId: string,
  abortSignal?: AbortSignal,
): Promise<ParticipantListLinkRow> {
  const response = await przezmorzeApi.put<ParticipantListLinkRow>(
    `/orders/${orderId}/participant-lists/links/${linkId}/revoke`,
    {},
    { signal: abortSignal },
  );
  return response.data;
}
